import { deserialiseAssetManifest, frameId, serialiseAssetManifest, type AssetManifest } from "../assets/manifest.ts";
import type { CompileContext, FrameDimensions, TransitionStrip } from "../compile/context.ts";
import type { PropFootprint } from "../compile/footprint.ts";
import type { FrameDepth } from "../compile/ground-depth.ts";
import type { FrameMarks } from "../compile/stand-map.ts";
import { asArray, asInteger, asRecord, asString } from "../document/validate.ts";
import type { TilesetRef } from "../document/types.ts";
import type { RuleSet } from "../terrain/rules.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import type { CataloguePackInfo, CataloguePropInfo, CatalogueTilesetInfo, EditorCatalogue, PropPlayback } from "./catalogue.ts";

/**
 * Art that arrives after the editor's atlas was loaded (ADR-129 decisions 3.7 and 3.8, ADR-130 decisions 2.7 and
 * 2.8): what a request names, what the route answers with the sheet, which picks a tab still lacks, and how a
 * catalogue and a compile context grow by what arrived. All of it is plain data and pure, so the server that cuts
 * the frames and the tab that stamps them read one description.
 */

/**
 * The most tiles one request names, and so the most frames one sheet carries by request (a strip's other frames
 * come with the frame that starts it). It is the cap of an operation's picks, so one pull is one request.
 */
export const LATE_FRAMES_PER_FETCH = 256;

const SCHEMA_VERSION = 1;
const MAX_ID_UNITS = 256;
const MAX_TILE = 65535;
const QUERY_FIELDS = ["tileset", "tiles"];

/**
 * What the route answers, as the file `dist/pack-index/frames/<hash>.json` holds it. `assets` is an asset
 * manifest whose pages are the sheets (a path under `dist/` and the size of each) and whose frames are each
 * frame's rect on its sheet with every field the compile's atlas gives it (`base`, `art`, `ground`, `noFade`,
 * `empty`, `depth`), padded by the extrusion as the atlas pads. The rest are the entries the store takes: the
 * catalogue's props, tileset and pack, and the compile context's footprints, derived footprints, frame sizes,
 * saved depths and transition strips for those frames. `missing` lists the tiles asked for that the pack does
 * not hold.
 */
export interface LateFramesDescriptor {
  readonly schemaVersion: 1;
  readonly tileset: string;
  readonly assets: AssetManifest;
  readonly pack: CataloguePackInfo;
  readonly tilesets: readonly CatalogueTilesetInfo[];
  readonly props: readonly CataloguePropInfo[];
  readonly footprints: Readonly<Record<string, PropFootprint>>;
  readonly derivedFootprints: Readonly<Record<string, PropFootprint | null>>;
  readonly frameSizes: Readonly<Record<string, FrameDimensions>>;
  readonly savedDepths: Readonly<Record<string, FrameDepth>>;
  readonly standMarks?: Readonly<Record<string, FrameMarks>>;
  readonly transitionStrips: Readonly<Record<string, TransitionStrip>>;
  readonly missing: readonly number[];
}

/**
 * The part of a descriptor a store grows by.
 */
export type LibraryGrowth = Pick<LateFramesDescriptor, "tilesets" | "props" | "footprints" | "derivedFootprints" | "frameSizes" | "savedDepths" | "standMarks" | "transitionStrips"> & {
  readonly packs: readonly CataloguePackInfo[];
  readonly terrains?: readonly TerrainDefinition[];
  readonly ruleSets?: readonly RuleSet[];
};

/**
 * One request: a tileset and the tiles asked of it, ascending.
 */
export interface LateRequest {
  readonly tileset: string;
  readonly tiles: readonly number[];
}

/**
 * The query string of a request, as the route reads it.
 *
 * @param tileset The tileset id.
 * @param tiles The local tile ids, in any order.
 * @returns `tileset=<id>&tiles=<ascending list>`.
 */
export function lateFramesQuery(tileset: string, tiles: readonly number[]): string {
  return `tileset=${encodeURIComponent(tileset)}&tiles=${[...tiles].sort((a, b) => a - b).join(",")}`;
}

/**
 * Reads and checks a request's query: exactly the two fields, each once; a tileset id of 1 to 256 units; at
 * least one and at most `LATE_FRAMES_PER_FETCH` tiles, whole numbers from 0 to 65,535 written without a leading
 * zero, ascending and distinct.
 *
 * @param query The request's query as decoded name and value pairs, in the order received.
 * @returns The request, or the sentence that refuses it.
 */
export function readLateFramesQuery(query: readonly (readonly [string, string])[]): LateRequest | string {
  const keys = query.map(([key]) => key);
  if (keys.length !== QUERY_FIELDS.length || !QUERY_FIELDS.every((field) => keys.filter((key) => key === field).length === 1)) {
    return "the request takes tileset and tiles, each once, and no other field";
  }
  const valueOf = (field: string): string => (query.find(([key]) => key === field) as readonly [string, string])[1];
  const tileset = valueOf("tileset");
  if (tileset.length < 1 || tileset.length > MAX_ID_UNITS) {
    return `the tileset id is 1 to ${MAX_ID_UNITS} characters`;
  }
  const list = valueOf("tiles");
  const parts = list === "" ? [] : list.split(",");
  if (parts.length < 1 || parts.length > LATE_FRAMES_PER_FETCH) {
    return `tiles holds 1 to ${LATE_FRAMES_PER_FETCH} numbers`;
  }
  const tiles: number[] = [];
  for (const part of parts) {
    if (!/^(0|[1-9][0-9]{0,4})$/.test(part) || Number(part) > MAX_TILE) {
      return `a tile is a whole number from 0 to ${MAX_TILE}`;
    }
    const tile = Number(part);
    if (tiles.length > 0 && tile <= (tiles[tiles.length - 1] as number)) {
      return "tiles are ascending and distinct";
    }
    tiles.push(tile);
  }
  return { tileset, tiles };
}

/**
 * Reads a descriptor's tileset entries.
 *
 * @param value The parsed `tilesets` member.
 * @returns The entries.
 * @throws Error naming the path when an entry has the wrong shape.
 */
export function readCatalogueTilesets(value: unknown): CatalogueTilesetInfo[] {
  return asArray(value, "tilesets").map((item, i): CatalogueTilesetInfo => {
    const each = asRecord(item, `tilesets[${i}]`);
    const role = each["role"];
    if (role !== "terrain" && role !== "props") {
      throw new Error(`tilesets[${i}].role must be terrain or props`);
    }
    return { id: asString(each["id"], `tilesets[${i}].id`), name: asString(each["name"], `tilesets[${i}].name`), role };
  });
}

/**
 * Reads a descriptor's prop entries.
 *
 * @param value The parsed `props` member.
 * @returns The entries.
 * @throws Error naming the path when an entry has the wrong shape.
 */
export function readCataloguePropEntries(value: unknown): CataloguePropInfo[] {
  return asArray(value, "props").map((item, i): CataloguePropInfo => {
    const each = asRecord(item, `props[${i}]`);
    const plays = each["plays"];
    if (plays !== undefined && plays !== "static" && plays !== "loop" && plays !== "once") {
      throw new Error(`props[${i}].plays must be static, loop or once`);
    }
    return {
      tileset: asString(each["tileset"], `props[${i}].tileset`),
      tile: asInteger(each["tile"], `props[${i}].tile`),
      name: asString(each["name"], `props[${i}].name`),
      group: asString(each["group"], `props[${i}].group`),
      ...(each["path"] === undefined ? {} : { path: asString(each["path"], `props[${i}].path`) }),
      ...(plays === undefined ? {} : { plays: plays as PropPlayback }),
      ...(each["draft"] === true ? { draft: true as const } : {}),
    };
  });
}

/**
 * Reads the file a route answers.
 *
 * @param text The file's text.
 * @returns The descriptor.
 * @throws Error naming the path when the text is not JSON, holds another version, lacks a part or holds a part of
 *   the wrong shape, or its manifest refuses (a frame that leaves its sheet, an id of the wrong form).
 */
export function readLateFrames(text: string): LateFramesDescriptor {
  const record = asRecord(JSON.parse(text) as unknown, "late frames");
  if (record["schemaVersion"] !== SCHEMA_VERSION) {
    throw new Error(`late frames schemaVersion must be ${SCHEMA_VERSION}`);
  }
  const assets = deserialiseAssetManifest(JSON.stringify(asRecord(record["assets"], "assets")));
  const pack = asRecord(record["pack"], "pack");
  const tilesets = readCatalogueTilesets(record["tilesets"]);
  const props = readCataloguePropEntries(record["props"]);
  return {
    schemaVersion: SCHEMA_VERSION,
    tileset: asString(record["tileset"], "tileset"),
    assets,
    pack: { id: asString(pack["id"], "pack.id"), name: asString(pack["name"], "pack.name") },
    tilesets,
    props,
    footprints: asRecord(record["footprints"], "footprints") as Readonly<Record<string, PropFootprint>>,
    derivedFootprints: asRecord(record["derivedFootprints"], "derivedFootprints") as Readonly<Record<string, PropFootprint | null>>,
    frameSizes: asRecord(record["frameSizes"], "frameSizes") as Readonly<Record<string, FrameDimensions>>,
    savedDepths: asRecord(record["savedDepths"], "savedDepths") as Readonly<Record<string, FrameDepth>>,
    ...(record["standMarks"] === undefined ? {} : { standMarks: asRecord(record["standMarks"], "standMarks") as unknown as Readonly<Record<string, FrameMarks>> }),
    transitionStrips: asRecord(record["transitionStrips"], "transitionStrips") as Readonly<Record<string, TransitionStrip>>,
    missing: asArray(record["missing"], "missing").map((tile, i) => asInteger(tile, `missing[${i}]`)),
  };
}

/**
 * The text of a descriptor, with its manifest written in the manifest's own order and form.
 *
 * @param descriptor The descriptor.
 * @returns JSON text, one line.
 */
export function serialiseLateFrames(descriptor: LateFramesDescriptor): string {
  return JSON.stringify({ ...descriptor, assets: JSON.parse(serialiseAssetManifest(descriptor.assets)) as unknown });
}

/**
 * The requests a tab still has to make: the picked tiles of the world's partial entries whose frames it does not
 * hold and has not asked for. Whole entries are in the editor's atlas and are never asked for. A long entry is cut
 * into requests of at most `LATE_FRAMES_PER_FETCH` ascending tiles, the same cuts for the same document, so the
 * browser's copy of an earlier answer serves a later request; the entries keep the tileset list's order.
 *
 * @param tilesets The world's tileset list.
 * @param held Whether the tab holds a frame, by frame id.
 * @param asked The frame ids already asked for.
 * @returns The requests, none when nothing is lacking.
 */
export function missingPicks(tilesets: readonly TilesetRef[], held: (frame: string) => boolean, asked: ReadonlySet<string>): LateRequest[] {
  const requests: LateRequest[] = [];
  for (const entry of tilesets) {
    if (entry.frames === undefined) {
      continue;
    }
    const lacking = entry.frames.filter((tile) => {
      const id = frameId(entry.id, null, tile);
      return !held(id) && !asked.has(id);
    });
    for (let from = 0; from < lacking.length; from += LATE_FRAMES_PER_FETCH) {
      requests.push({ tileset: entry.id, tiles: lacking.slice(from, from + LATE_FRAMES_PER_FETCH) });
    }
  }
  return requests;
}

/**
 * A catalogue grown by a descriptor: the pack, the tileset and the props it lists that the catalogue does not
 * already list, appended in the descriptor's order, and the arrived frames taken off the lost list. The catalogue
 * given is never changed.
 *
 * @param catalogue The catalogue now.
 * @param growth What arrived.
 * @returns The grown catalogue.
 */
export function grownCatalogue(catalogue: EditorCatalogue, growth: Pick<LibraryGrowth, "packs" | "tilesets" | "props">): EditorCatalogue {
  const listed = new Set(catalogue.props.map((prop) => frameId(prop.tileset, null, prop.tile)));
  const fresh = growth.props.filter((prop) => !listed.has(frameId(prop.tileset, null, prop.tile)));
  const tilesetIds = new Set(catalogue.tilesets.map((tileset) => tileset.id));
  const packs = catalogue.packs ?? [];
  const arrived = new Set(growth.props.map((prop) => frameId(prop.tileset, null, prop.tile)));
  const lost = catalogue.lostPulls?.filter((frame) => !arrived.has(frameId(frame.tileset, null, frame.tile)));
  const { lostPulls: _lost, ...rest } = catalogue;
  return {
    ...rest,
    tilesets: [...catalogue.tilesets, ...growth.tilesets.filter((tileset) => !tilesetIds.has(tileset.id))],
    props: [...catalogue.props, ...fresh],
    packs: [...packs, ...growth.packs.filter((pack, i) => !packs.some((held) => held.id === pack.id) && growth.packs.findIndex((other) => other.id === pack.id) === i)],
    ...(lost === undefined || lost.length === 0 ? {} : { lostPulls: lost }),
  };
}

/**
 * A compile context grown by a descriptor: footprints, derived footprints, frame sizes, saved depths and
 * transition strips of the arrived frames, and the terrains and rule sets that came with them, each added only
 * where the context holds no entry for the frame, terrain or rule set, so an edit made in the tab stands and a
 * terrain the editor opened with is never replaced. A new value; the context given is never changed, and every
 * other part of it is the same object.
 *
 * @param context The context now.
 * @param growth What arrived.
 * @returns The grown context.
 */
export function grownContext(context: CompileContext, growth: Pick<LibraryGrowth, "footprints" | "derivedFootprints" | "frameSizes" | "savedDepths" | "standMarks" | "transitionStrips" | "terrains" | "ruleSets">): CompileContext {
  const terrainIds = new Set(context.terrains.map((terrain) => terrain.id));
  const ruleSetIds = new Set(context.ruleSets.map((ruleSet) => ruleSet.id));
  const terrains = (growth.terrains ?? []).filter((terrain) => !terrainIds.has(terrain.id));
  const ruleSets = (growth.ruleSets ?? []).filter((ruleSet) => !ruleSetIds.has(ruleSet.id));
  return {
    ...context,
    ...(terrains.length === 0 ? {} : { terrains: [...context.terrains, ...terrains] }),
    ...(ruleSets.length === 0 ? {} : { ruleSets: [...context.ruleSets, ...ruleSets] }),
    collision: Object.keys(growth.footprints).length === 0 && context.collision.footprints === undefined ? context.collision : { ...context.collision, footprints: unheld(context.collision.footprints, growth.footprints) },
    ...(Object.keys(growth.frameSizes).length === 0 && context.frameSizes === undefined ? {} : { frameSizes: unheld(context.frameSizes, growth.frameSizes) }),
    ...(Object.keys(growth.derivedFootprints).length === 0 && context.derivedFootprints === undefined ? {} : { derivedFootprints: unheld(context.derivedFootprints, growth.derivedFootprints) }),
    ...(Object.keys(growth.savedDepths).length === 0 && context.savedDepths === undefined ? {} : { savedDepths: unheld(context.savedDepths, growth.savedDepths) }),
    ...(Object.keys(growth.standMarks ?? {}).length === 0 && context.standMarks === undefined ? {} : { standMarks: unheld(context.standMarks, growth.standMarks ?? {}) }),
    ...(Object.keys(growth.transitionStrips).length === 0 && context.transitionStrips === undefined ? {} : { transitionStrips: unheld(context.transitionStrips, growth.transitionStrips) }),
  };
}

/**
 * A record with the entries of `fresh` the record lacks added.
 *
 * @param held The record now, or undefined.
 * @param fresh The entries that arrived.
 * @returns A new record.
 */
function unheld<T>(held: Readonly<Record<string, T>> | undefined, fresh: Readonly<Record<string, T>>): Record<string, T> {
  const next: Record<string, T> = { ...(held ?? {}) };
  for (const id of Object.keys(fresh)) {
    if (!Object.hasOwn(next, id)) {
      next[id] = fresh[id] as T;
    }
  }
  return next;
}
